// Food_Log write path — Phase 5, calculation layer.
//
// Pure functions only. This module performs NO I/O: it does not import figApi,
// foodLog, or React, and it never touches the network, localStorage, or a
// Date. It turns a catalog entry plus what the user typed into the exact object
// createItem expects, and nothing else. Everything here is synchronously
// testable with no mocks, which is the whole reason it is a separate file from
// the sheet that will call it.
//
// The one rule that matters most in here is the null/zero distinction, and it
// is enforced in nutrientsForGrams. Read that comment before changing anything.
//
// Rounding happens NOWHERE. The Food_Log number columns are decimalPlaces
// automatic and round-trip floats exactly, so a value is stored as computed. A
// display layer may round; the snapshot must not, because a rounded snapshot
// cannot be un-rounded later and the daily total would drift by the error on
// every row.

import foodData from './foodData';

// per100g key -> Food_Log column name, and the order here is the order they
// land in the row object.
//
// The keys stay clean and unit-free; the COLUMN name carries the unit where it
// is not milligrams (see the naming rule in figApi's Food_Log definition). This
// table is exactly where that translation belongs — the catalog should not have
// to spell units into its own field names, and SharePoint should not have to
// guess them.
//
// Growing this table is the only thing adding a nutrient requires. The omission
// logic below does not change: an entry whose per100g lacks `iodine` produces a
// row with no IodineMcg key at all, which is the correct reading of "nobody has
// sourced this yet" and is what every entry does until values land.
const PER100G_TO_COLUMN = {
  kcal:      'Calories',
  protein:   'Protein',
  fat:       'Fat',
  carbs:     'Carbs',
  sodium:    'Sodium',
  potassium: 'Potassium',
  magnesium: 'Magnesium',
  iodine:    'IodineMcg',
  calcium:   'Calcium',
  retinol:   'RetinolMcg',
};

// Units where the resulting Grams is a measurement end to end. Anything else —
// 'wing', 'slice', 'stick', 'cup', 'tbsp' — is a count or a volume, which is to
// say an estimate of mass however confidently it is stated.
//
// 'g bone-in' is deliberately NOT here, and it is the interesting exclusion.
// The plate genuinely went on the scale, so that half is exact. What follows is
// not: the bone subtraction multiplies the piece count by
// entry.boneWeightPerUnit, and for the only entry that has one that constant
// rests on a single batch of three wings weighed on 2026-08-03. It is measured
// rather than invented — the catalog note carries the raw numbers — but n=3
// from one cook, one grade of wing and one operator is not a settled figure,
// and the note says so outright.
//
// So the Grams every nutrient on the row is scaled from still contains an
// unsettled constant. Subtracting an unsettled number from an exact one does
// not produce a measurement, and the row logs as Estimated on that basis. The
// objection is to the sample size, not to the provenance.
//
// Revisit once the wings have been re-weighed across a few more batches. If the
// constant holds across them, this line is the one to change.
export const WEIGHT_UNITS = new Set(['g', 'oz', 'lb']);

// Catalog lookup by id. The only reason this module imports foodData: a caller
// editing an existing Food_Log row has a FoodId string, not an entry object.
export function findEntry(id) {
  return foodData.find(e => e.id === id) || null;
}

// Grams actually eaten, as a raw float.
//
// Throws rather than guessing. Every throw here is a call-site bug or a catalog
// data bug, and a silently plausible number written into a permanent snapshot
// row is far worse than a failed save the user can see and retry.
export function resolveGrams(entry, unitName, qty, count) {
  const unitDef = entry.units.find(u => u.unit === unitName);
  if (!unitDef) {
    throw new Error(
      `${entry.id}: unknown unit '${unitName}'. ` +
      `Known units: ${entry.units.map(u => u.unit).join(', ')}`
    );
  }
  if (!Number.isFinite(qty) || qty <= 0) {
    throw new Error(
      `${entry.id}.${unitName}: quantity must be a finite number greater than 0, got ${qty}`
    );
  }

  if (unitDef.requiresCount === true) {
    // A missing count is NOT zero. Treating it as zero would skip the bone
    // subtraction entirely and log the full plate weight — 420g of wings and
    // bones recorded as 420g of meat, which is ~36% high and permanent.
    if (!Number.isFinite(count) || count < 1) {
      throw new Error(
        `${entry.id}.${unitName}: this unit requires a piece count of at least 1, got ${count}. ` +
        `The count is what the bone subtraction is computed from; it cannot be defaulted.`
      );
    }
    // A unit asking for a count on an entry with no bone constant is a catalog
    // bug, not a runtime condition. Caught here because the arithmetic below
    // would otherwise produce NaN and NaN would sail into the row.
    if (entry.boneWeightPerUnit === undefined) {
      throw new Error(
        `${entry.id}.${unitName}: unit sets requiresCount but the entry has no ` +
        `boneWeightPerUnit. This is a foodData defect — fix the catalog, not the caller.`
      );
    }
    const grams = qty * unitDef.gramsPerUnit - count * entry.boneWeightPerUnit;
    if (grams <= 0) {
      throw new Error(
        `${entry.id}.${unitName}: bone weight meets or exceeds plate weight — ` +
        `${qty} * ${unitDef.gramsPerUnit} - ${count} * ${entry.boneWeightPerUnit} = ${grams}. ` +
        `Check the count and the plate weight.`
      );
    }
    return grams;
  }

  return qty * unitDef.gramsPerUnit;
}

// The seven nutrient columns for a given mass, with undisclosed nutrients
// OMITTED rather than zeroed.
//
// This is the function the whole read path depends on. In JavaScript:
//
//   null * 1.7      === 0      // not NaN. Silently.
//   undefined * 1.7 === NaN
//
// So the obvious `per100g[key] * grams / 100` turns "the source did not
// disclose magnesium" into "this food contains 0mg magnesium" with no error and
// no warning, and writes that fiction into a snapshot row that is never
// recomputed. The daily total then reads low forever and looks perfectly
// healthy doing it. That is the exact failure totalNutrients in foodLog.js
// exists to make visible, and it can only stay visible if the key is ABSENT.
//
// Absent, not null: figApi's createItem iterates Object.entries(fields), so a
// key that is not present is never sent and the SharePoint column stays unset,
// while an explicit null IS sent. Only omission produces the undefined that
// foodLog's isDisclosed() reads as undisclosed.
//
// A literal 0 is the opposite case and MUST survive. beef-tallow really does
// contain 0g protein and lmnt really does contain 0g carbs; those are measured
// zeros the source asserted, and they are included as 0.
export function nutrientsForGrams(entry, grams) {
  const out = {};
  for (const [key, column] of Object.entries(PER100G_TO_COLUMN)) {
    const v = entry.per100g[key];
    if (v === null || v === undefined) continue; // omitted — see above
    out[column] = v * grams / 100;
  }
  return out;
}

// 'Catalog' means every number on this row came from a source that was read
// directly, scaled from a mass that was actually measured. All three conditions
// have to hold:
//
//   confidence === 'verified'  — spec §3: anything not verified logs as
//                                Estimated. 'good' and 'estimated' entries
//                                carry documented ambiguity in their own notes.
//   a weight unit              — spec §5: count-based amounts ('wing', 'slice',
//                                'stick') and volumes ('cup', 'tbsp') are
//                                conversions, not measurements, regardless of
//                                how good the per100g figures are.
//   qtyTouched === true        — accepting a prefilled defaultQty without
//                                editing it is the eyeball tier. The user did
//                                not weigh anything; they accepted a guess the
//                                catalog made on their behalf.
//
// Any one of them false makes the row an estimate. Strict === true on
// qtyTouched so that undefined from a caller that forgot to pass it lands on
// 'Estimated' — the safe side.
export function entrySourceFor(entry, unitName, qtyTouched) {
  const verified = entry.confidence === 'verified';
  const weighed = WEIGHT_UNITS.has(unitName);
  return verified && weighed && qtyTouched === true ? 'Catalog' : 'Estimated';
}

// The exact object to hand to createItem(token, 'Food_Log', fields).
//
// Date and Time are passed through as the plain strings they arrive as. Both
// have guards on the figApi side (toDateOnly, toTimeOnly) and reformatting
// either one here would mean two places could disagree about the format. In
// particular: no toISOString() anywhere near Date, and no clock formatting
// anywhere near Time.
//
// MealSlot and Notes are deliberately absent. MealSlot still exists on the list
// and in LIST_DEFINITIONS but nothing reads it since the ledger replaced meal
// grouping; writing it would quietly restart a dataset nothing consumes.
export function buildLogRow({ entry, dateStr, time, qty, unit, count, qtyTouched }) {
  // Title is required:true on the list. A create without it fails with a 400
  // from Graph, so it is caught here where the entry id can be named. No
  // placeholder substitution — an untitled row is a catalog defect to fix, not
  // a row to invent a name for.
  if (typeof entry.name !== 'string' || entry.name.trim() === '') {
    throw new Error(
      `${entry.id}: entry.name is empty or not a string, and Food_Log.Title is required.`
    );
  }

  const grams = resolveGrams(entry, unit, qty, count);

  return {
    Title:    entry.name,
    Date:     dateStr,
    Time:     time,
    FoodId:   entry.id,
    Quantity: qty,
    Unit:     unit,
    Grams:    grams,
    ...nutrientsForGrams(entry, grams),
    EntrySource: entrySourceFor(entry, unit, qtyTouched),
  };
}
